import { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { ConfigContext } from '../shared/ConfigContext';
import { changeLanguage } from '../../i18n';

// companyName has no field on this page (admins set it in Edit User), but it
// must round-trip through the save payload or saving here would wipe it.
const EMPTY_SHIPPING = {
  recipientName: '', companyName: '', phone: '', addressLine1: '', addressLine2: '',
  city: '', state: '', postalCode: '', country: '', deliveryNotes: '',
};

const normalizeShippingAddress = (shippingAddress) => Object.keys(EMPTY_SHIPPING).reduce((acc, key) => {
  acc[key] = shippingAddress?.[key] ?? '';
  return acc;
}, {});

const useUserProfilePage = () => {
  const { t } = useTranslation();
  const config = useContext(ConfigContext);
  const canEditShipping = localStorage.getItem('userRole') === 'user';

  const [status, setStatus] = useState('loading');
  const [availableLanguages, setAvailableLanguages] = useState([
    ['en', 'English'], ['fr', 'Français'], ['fr-CA', 'Français (Canada)'], ['tr', 'Türkçe'],
  ]);

  const emptyForm = { username: '', email: '', preferredLanguage: 'en', shipping: EMPTY_SHIPPING };
  const [form, setForm] = useState(emptyForm);
  const initialFormRef = useRef(emptyForm);
  const [shippingLoaded, setShippingLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const fetchProfile = useCallback(async () => {
    if (!config?.backendUrl) return;
    const userId = localStorage.getItem('userId');
    if (!userId) {
      notify(t('User not authenticated'), 'error');
      setStatus('ready');
      return;
    }

    setStatus('loading');
    try {
      const response = await axios.get(`${config.backendUrl}/user_profile`, { headers: { 'X-User-Id': userId } });
      const loaded = {
        username: response.data.username || '',
        email: response.data.email || '',
        preferredLanguage: response.data.preferredLanguage || 'en',
        shipping: normalizeShippingAddress(response.data.shippingAddress),
      };
      setForm(loaded);
      initialFormRef.current = loaded;
      setShippingLoaded(true);
      if (response.data.availableLanguages?.length > 0) {
        setAvailableLanguages(response.data.availableLanguages);
      }
      setStatus('ready');
    } catch (error) {
      console.error('Error fetching user profile:', error);
      const fallback = {
        username: localStorage.getItem('username') || '',
        email: '',
        preferredLanguage: localStorage.getItem('userLanguage') || 'en',
        shipping: EMPTY_SHIPPING,
      };
      setForm(fallback);
      initialFormRef.current = fallback;
      notify(t('Failed to load user profile from server. Using local data.'), 'warning');
      setStatus('ready');
    }
  }, [config, t]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const isDirty = JSON.stringify(form) !== JSON.stringify(initialFormRef.current);
  const discardChanges = () => setForm(initialFormRef.current);

  const handleSave = async () => {
    if (!form.username.trim()) {
      notify(t('Username is required'), 'error');
      return;
    }
    setSaving(true);
    try {
      const userId = localStorage.getItem('userId');
      const payload = {
        username: form.username,
        email: form.email,
        preferredLanguage: form.preferredLanguage,
      };
      if (canEditShipping && shippingLoaded) {
        payload.shippingAddress = form.shipping;
      }
      const response = await axios.put(`${config.backendUrl}/user_profile`, payload, { headers: { 'X-User-Id': userId } });

      let confirmedForm = form;
      if (payload.shippingAddress) {
        const confirmedShipping = response.data?.shippingAddress;
        if (!Object.prototype.hasOwnProperty.call(response.data || {}, 'shippingAddress') ||
          typeof confirmedShipping !== 'object' || confirmedShipping === null) {
          throw new Error('Shipping address was not confirmed by the server');
        }
        confirmedForm = { ...form, shipping: normalizeShippingAddress(confirmedShipping) };
        setForm(confirmedForm);
      }
      initialFormRef.current = confirmedForm;

      localStorage.setItem('username', form.username);
      localStorage.setItem('userLanguage', form.preferredLanguage);
      await changeLanguage(form.preferredLanguage);

      notify(t('Profile updated successfully'));
    } catch (error) {
      console.error('Error updating profile:', error);
      notify(error.response?.data?.error || t('Failed to update profile'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return {
    t, status, availableLanguages, canEditShipping,
    form, setForm, saving, isDirty, discardChanges, handleSave,
    snackbar, setSnackbar,
  };
};

export default useUserProfilePage;
